/** The schema editor's two-column layout (CP-7988, from Allison's mockup):
 *  property groups on the left, Ungrouped on the right.
 *
 *  Properties drag within and between them — several at once from Ungrouped
 *  when checked — and every move goes through `moveProperties`, which sets
 *  `x-group` and renumbers `x-displayOrder`. Rows stay put while dragging; a
 *  line shows where the drop lands. A row opens the full property editor
 *  inline. Collapsed groups, open rows, the filter and the selection are view
 *  state only, never saved.
 */
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, type SortingStrategy } from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  type Theme,
} from '@mui/material';
import { useMemo, useRef, useState, type ReactNode } from 'react';

import { SchemaPropertyFields } from './SchemaPropertyEditor';
import {
  addGroup,
  addProperty,
  moveGroup,
  moveProperties,
  propertyLists,
  removeGroup,
  removeProperty,
  renameProperty,
  resolveDrop,
  setProperty,
  setRequired,
  updateGroup,
  type PropertyList,
} from './schemaEditorOps';
import {
  SCHEMA_TYPES,
  UNGROUPED,
  X_HTML,
  X_MULTILINGUAL,
  X_TEMPLATE,
  groupsOf,
  isRequired,
  type JsonSchemaType,
  type SchemaGroup,
  type SchemaProp,
  type TagSchema,
} from './vocabulary';

const ROW_HEIGHT = 30;
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
/** Droppable id prefix of a whole column (a group card, or the Ungrouped panel). */
const ZONE = 'zone:';
/** Rows never shift while dragging; the drop line does the showing. */
const noShift: SortingStrategy = () => null;

type Drop = { target: string; beforeKey: string | null };
type KeySet = ReadonlySet<string>;

const slugify = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const toggled = (set: KeySet, key: string): KeySet => {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};
const dropShadow = (t: Theme) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.12)}`;

/** Pointer: the row under the pointer, else the column it is over. Keyboard
 *  (no pointer): the nearest target. */
const collision: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  if (within.length) {
    const rows = within.filter((c) => !String(c.id).startsWith(ZONE));
    return rows.length ? rows : within;
  }
  return closestCenter(args);
};

interface Props {
  schema: TagSchema;
  readOnly: boolean;
  onChange: (next: TagSchema) => void;
}

export function SchemaBoard({ schema, readOnly, onChange }: Props) {
  const lists = useMemo(() => propertyLists(schema), [schema]);
  const groups = useMemo(() => groupsOf(schema), [schema]);
  const props = schema.properties ?? {};
  const grouped = lists.filter((l) => l.group);
  const ungrouped = lists.find((l) => l.id === UNGROUPED) ?? { id: UNGROUPED, group: null, keys: [] };

  const [collapsed, setCollapsed] = useState<KeySet>(() => new Set());
  const [open, setOpen] = useState<KeySet>(() => new Set());
  const [selected, setSelected] = useState<KeySet>(() => new Set());
  const [filter, setFilter] = useState('');
  const [moveTarget, setMoveTarget] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [newGroup, setNewGroup] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newType, setNewType] = useState<JsonSchemaType>('string');
  const [dragKeys, setDragKeys] = useState<string[] | null>(null);
  const [drop, setDrop] = useState<Drop | null>(null);
  // dnd-kit may call a handler from the render before setDragKeys lands.
  const dragRef = useRef<string[] | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const selectedKeys = ungrouped.keys.filter((k) => selected.has(k));
  const target = groups.some((g) => g.name === moveTarget) ? moveTarget : (groups[0]?.name ?? '');
  const q = filter.trim().toLowerCase();
  const shownUngrouped = q ? ungrouped.keys.filter((k) => k.toLowerCase().includes(q)) : ungrouped.keys;

  const move = (keys: string[], to: string, beforeKey: string | null) => {
    const next = moveProperties(schema, keys, to, beforeKey);
    if (next !== schema) onChange(next);
    setSelected((prev) => new Set([...prev].filter((k) => !keys.includes(k))));
  };

  const dropFor = (event: DragMoveEvent): Drop | null => {
    const keys = dragRef.current;
    const { active, over } = event;
    if (!keys || !over) return null;
    const id = String(over.id);
    if (id.startsWith(ZONE)) return resolveDrop(lists, keys, { container: id.slice(ZONE.length) }, false);
    let after: boolean;
    if (event.activatorEvent instanceof KeyboardEvent) {
      // A keyboard step lands on a row: moving down within a list goes below it.
      const from = lists.find((l) => l.keys.includes(String(active.id)));
      after = !!from && from.keys.includes(id) && from.keys.indexOf(id) > from.keys.indexOf(String(active.id));
    } else {
      const r = active.rect.current.translated;
      after = !!r && r.top + r.height / 2 > over.rect.top + over.rect.height / 2;
    }
    return resolveDrop(lists, keys, { key: id }, after);
  };

  const track = (event: DragMoveEvent) => {
    const d = dropFor(event);
    setDrop((prev) => (prev?.target === d?.target && prev?.beforeKey === d?.beforeKey ? prev : d));
  };
  const endDrag = () => {
    dragRef.current = null;
    setDragKeys(null);
    setDrop(null);
  };
  const onDragStart = ({ active }: DragStartEvent) => {
    const key = String(active.id);
    const keys = selected.has(key) && ungrouped.keys.includes(key) ? selectedKeys : [key];
    dragRef.current = keys;
    setDragKeys(keys);
  };
  const onDragEnd = (event: DragEndEvent) => {
    const d = dropFor(event);
    const keys = dragRef.current;
    endDrag();
    if (d && keys) move(keys, d.target, d.beforeKey);
  };

  const addNewGroup = () => {
    const title = newGroup.trim();
    if (!title) return;
    let name = slugify(title) || 'group';
    const taken = new Set(groups.map((g) => g.name));
    if (taken.has(name)) {
      let i = 2;
      while (taken.has(`${name}_${i}`)) i += 1;
      name = `${name}_${i}`;
    }
    onChange(addGroup(schema, { name, title }));
    setNewGroup('');
  };
  const addNewProp = () => {
    const k = newKey.trim();
    if (!k) return;
    onChange(addProperty(schema, k, newType));
    setNewKey('');
  };

  const renderRows = (list: PropertyList, keys: string[], selectable: boolean) => (
    <SortableContext items={keys} strategy={noShift}>
      {keys.map((k) => {
        const index = list.keys.indexOf(k);
        const prop = props[k] ?? {};
        return (
          <PropertyRow
            key={k}
            k={k}
            prop={prop}
            index={index}
            readOnly={readOnly}
            required={isRequired(schema, k)}
            open={open.has(k)}
            dimmed={!!dragKeys?.includes(k)}
            dropBefore={drop?.target === list.id && drop.beforeKey === k}
            selectable={selectable && !readOnly}
            selected={selected.has(k)}
            onSelect={() => setSelected((prev) => toggled(prev, k))}
            onToggleOpen={() => setOpen((prev) => toggled(prev, k))}
            upDisabled={index <= 0}
            downDisabled={index >= list.keys.length - 1}
            onUp={() => move([k], list.id, list.keys[index - 1] ?? null)}
            onDown={() => move([k], list.id, list.keys[index + 2] ?? null)}
          >
            <SchemaPropertyFields
              propKey={k}
              prop={prop}
              readOnly={readOnly}
              groups={groups}
              required={isRequired(schema, k)}
              onRename={(nk) => {
                const next = renameProperty(schema, k, nk);
                if (next === schema) return;
                onChange(next);
                setOpen((prev) => new Set([...prev].map((x) => (x === k ? nk.trim() : x))));
              }}
              onChange={(next: SchemaProp) => onChange(setProperty(schema, k, next))}
              onRemove={() => onChange(removeProperty(schema, k))}
              onToggleRequired={(v) => onChange(setRequired(schema, k, v))}
            />
          </PropertyRow>
        );
      })}
      {drop?.target === list.id && drop.beforeKey === null && keys.length > 0 && <DropLine />}
    </SortableContext>
  );

  const groupedCount = grouped.reduce((n, l) => n + l.keys.length, 0);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      onDragStart={onDragStart}
      onDragMove={track}
      onDragOver={track}
      onDragEnd={onDragEnd}
      onDragCancel={endDrag}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, alignItems: 'flex-start' }}>
        <Box component="section" aria-label="Property groups" sx={{ flex: '1 1 460px', minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Property groups</Typography>
            <Typography variant="caption" color="text.secondary">
              {plural(grouped.length, 'group')} · {groupedCount} grouped · {ungrouped.keys.length} ungrouped
            </Typography>
            {grouped.length > 0 && (
              <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
                <Button size="small" variant="outlined" onClick={() => setCollapsed(new Set(grouped.map((l) => l.id)))}>
                  Collapse all
                </Button>
                <Button size="small" variant="outlined" onClick={() => setCollapsed(new Set())}>
                  Expand all
                </Button>
              </Stack>
            )}
          </Stack>

          <Stack spacing={1.25}>
            {grouped.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No groups yet. Add one, then drag properties into it from Ungrouped.
              </Typography>
            )}
            {grouped.map((list, i) => (
              <GroupCard
                key={list.id}
                list={list}
                group={list.group!}
                readOnly={readOnly}
                collapsed={collapsed.has(list.id)}
                active={drop?.target === list.id}
                editing={editing === list.id}
                upDisabled={i === 0}
                downDisabled={i === grouped.length - 1}
                onToggle={() => setCollapsed((prev) => toggled(prev, list.id))}
                onEdit={() => setEditing((cur) => (cur === list.id ? null : list.id))}
                onUpdate={(patch) => onChange(updateGroup(schema, list.id, patch))}
                onUp={() => onChange(moveGroup(schema, list.id, -1))}
                onDown={() => onChange(moveGroup(schema, list.id, 1))}
                onRemove={() => onChange(removeGroup(schema, list.id))}
              >
                {renderRows(list, list.keys, false)}
              </GroupCard>
            ))}
            {!readOnly && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ p: 1.25, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 2 }}
              >
                <TextField
                  size="small" placeholder="New group title" value={newGroup} fullWidth
                  inputProps={{ 'aria-label': 'New group title' }}
                  onChange={(e) => setNewGroup(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewGroup(); } }}
                />
                <Button size="small" startIcon={<AddIcon />} onClick={addNewGroup} disabled={!newGroup.trim()} sx={{ flexShrink: 0 }}>
                  Add group
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>

        <Box component="aside" aria-label="Ungrouped properties" sx={{ flex: '1 1 340px', minWidth: 0, position: 'sticky', top: 16 }}>
          <Zone id={UNGROUPED} disabled={readOnly} active={drop?.target === UNGROUPED}>
            <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Ungrouped</Typography>
                <CountPill text={q ? `${shownUngrouped.length} of ${ungrouped.keys.length}` : String(ungrouped.keys.length)} />
                {!readOnly && grouped.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    Drag into a group ←
                  </Typography>
                )}
              </Stack>
              <TextField
                size="small" fullWidth placeholder="Filter, e.g. 24h_pretour" value={filter}
                onChange={(e) => setFilter(e.target.value)}
                inputProps={{ 'aria-label': 'Filter ungrouped properties' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1.25 }}
              />
              {selectedKeys.length > 0 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{
                    mt: 1, px: 1, py: 0.75, borderRadius: 1, border: 1,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                    borderColor: (t) => alpha(t.palette.primary.main, 0.3),
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{selectedKeys.length} selected</Typography>
                  <TextField
                    select size="small" label="Move to" value={target} disabled={!groups.length}
                    onChange={(e) => setMoveTarget(e.target.value)} sx={{ minWidth: 170 }}
                  >
                    {groups.map((g) => (
                      <MenuItem key={g.name} value={g.name}>{g.title || g.name}</MenuItem>
                    ))}
                  </TextField>
                  <Button size="small" variant="contained" disabled={!target} onClick={() => move(selectedKeys, target, null)}>
                    Move
                  </Button>
                  <Button size="small" onClick={() => setSelected(new Set())} sx={{ ml: 'auto' }}>
                    Clear
                  </Button>
                </Stack>
              )}
            </Box>
            <Box sx={{ maxHeight: 640, overflowY: 'auto', p: '4px 6px 8px' }}>
              {renderRows(ungrouped, shownUngrouped, true)}
              {shownUngrouped.length === 0 && (
                <EmptyDrop active={drop?.target === UNGROUPED}>
                  {ungrouped.keys.length === 0
                    ? (readOnly ? 'Every property is in a group.' : 'Everything is grouped. Drag a property here to ungroup it.')
                    : 'No properties match this filter.'}
                </EmptyDrop>
              )}
            </Box>
            {!readOnly && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 1.5, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
                <TextField
                  size="small" placeholder="new_property_key" value={newKey} fullWidth
                  inputProps={{ 'aria-label': 'New property key', style: { fontFamily: MONO, fontSize: 12.5 } }}
                  onChange={(e) => setNewKey(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewProp(); } }}
                />
                <TextField
                  select size="small" value={newType} inputProps={{ 'aria-label': 'New property type' }}
                  onChange={(e) => setNewType(e.target.value as JsonSchemaType)} sx={{ width: 120, flexShrink: 0 }}
                >
                  {SCHEMA_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </TextField>
                <Button size="small" startIcon={<AddIcon />} onClick={addNewProp} disabled={!newKey.trim()} sx={{ flexShrink: 0 }}>
                  Add
                </Button>
              </Stack>
            )}
          </Zone>
        </Box>
      </Box>

      <DragOverlay dropAnimation={null}>
        {dragKeys?.[0] ? (
          <Paper
            elevation={4}
            sx={{ height: ROW_HEIGHT, px: 1, display: 'inline-flex', alignItems: 'center', gap: 1, borderRadius: 1, cursor: 'grabbing' }}
          >
            <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 500 }}>{dragKeys[0]}</Typography>
            {dragKeys.length > 1 && <CountPill text={`+${dragKeys.length - 1}`} />}
          </Paper>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/** A column's drop zone: the card itself, outlined while a drop would land in it. */
function Zone({ id, disabled, active, children }: { id: string; disabled: boolean; active: boolean; children: ReactNode }) {
  const { setNodeRef } = useDroppable({ id: `${ZONE}${id}`, disabled });
  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        borderColor: active ? 'primary.main' : 'divider',
        boxShadow: active ? dropShadow : 'none',
      }}
    >
      {children}
    </Paper>
  );
}

interface GroupCardProps {
  list: PropertyList;
  group: SchemaGroup;
  readOnly: boolean;
  collapsed: boolean;
  active: boolean;
  editing: boolean;
  upDisabled: boolean;
  downDisabled: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onUpdate: (patch: Partial<SchemaGroup>) => void;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
  children: ReactNode;
}

function GroupCard({
  list, group, readOnly, collapsed, active, editing, upDisabled, downDisabled,
  onToggle, onEdit, onUpdate, onUp, onDown, onRemove, children,
}: GroupCardProps) {
  const title = group.title || group.name;
  const count = list.keys.length;
  const preview = list.keys.slice(0, 3).join(', ') + (count > 3 ? `  +${count - 3} more` : '');
  const headerBg = (t: Theme) => (active && collapsed ? alpha(t.palette.primary.main, 0.06) : t.palette.grey[50]);
  return (
    <Zone id={list.id} disabled={readOnly} active={active}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.25, py: 0.75, minWidth: 0, bgcolor: headerBg }}>
        <IconButton size="small" onClick={onToggle} aria-expanded={!collapsed} aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${title}`} sx={{ p: 0.25 }}>
          <ExpandMoreIcon fontSize="small" sx={{ transition: 'transform 150ms', transform: collapsed ? 'rotate(-90deg)' : 'none' }} />
        </IconButton>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, flexShrink: 0, maxWidth: '45%' }}>{title}</Typography>
        <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: 'text.secondary', bgcolor: 'action.hover', borderRadius: 0.5, px: 0.75, flexShrink: 0 }}>
          {group.name}
        </Box>
        <CountPill text={String(count)} />
        {collapsed && count > 0 && (
          <Typography noWrap variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>{preview}</Typography>
        )}
        {!readOnly && (
          <Stack direction="row" sx={{ ml: 'auto', flexShrink: 0 }}>
            <IconButton size="small" onClick={onEdit} aria-pressed={editing} aria-label={`Edit the title and description of ${title}`}>
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={onUp} disabled={upDisabled} aria-label={`Move ${title} up`}>
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={onDown} disabled={downDisabled} aria-label={`Move ${title} down`}>
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" color="error" onClick={onRemove} aria-label={`Delete ${title} (its properties go back to Ungrouped)`}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        )}
      </Stack>
      {editing && !readOnly ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 1.25, pt: 0.5, pb: 1.25, bgcolor: 'grey.50' }}>
          <TextField size="small" label="Title" value={group.title ?? ''} onChange={(e) => onUpdate({ title: e.target.value })} sx={{ width: 220 }} />
          <TextField
            size="small" label="Description" value={group.description ?? ''} onChange={(e) => onUpdate({ description: e.target.value })}
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Stack>
      ) : group.description ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.25, pb: 0.75, bgcolor: 'grey.50' }}>
          {group.description}
        </Typography>
      ) : null}
      <Collapse in={!collapsed} unmountOnExit>
        <Box sx={{ p: '4px 6px 8px', borderTop: 1, borderColor: 'divider' }}>
          {children}
          {count === 0 && <EmptyDrop active={active}>{readOnly ? 'No properties in this group.' : 'Drop properties here'}</EmptyDrop>}
        </Box>
      </Collapse>
    </Zone>
  );
}

interface RowProps {
  k: string;
  prop: SchemaProp;
  index: number;
  readOnly: boolean;
  required: boolean;
  open: boolean;
  dimmed: boolean;
  dropBefore: boolean;
  selectable: boolean;
  selected: boolean;
  upDisabled: boolean;
  downDisabled: boolean;
  onSelect: () => void;
  onToggleOpen: () => void;
  onUp: () => void;
  onDown: () => void;
  children: ReactNode;
}

/** One property: the whole row drags with the mouse; its grip is the keyboard
 *  handle (Space, arrows, Space). The chevron opens the full editor below. */
function PropertyRow({
  k, prop, index, readOnly, required, open, dimmed, dropBefore, selectable, selected,
  upDisabled, downDisabled, onSelect, onToggleOpen, onUp, onDown, children,
}: RowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useSortable({ id: k, disabled: readOnly });
  const type = prop.type ?? 'string';
  return (
    <Box>
      {dropBefore && <DropLine />}
      <Box
        ref={setNodeRef}
        {...(readOnly ? {} : listeners)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, height: ROW_HEIGHT, px: 0.75, borderRadius: 1,
          cursor: readOnly ? 'default' : 'grab', userSelect: 'none', opacity: dimmed ? 0.4 : 1,
          bgcolor: (t) => (selected ? alpha(t.palette.primary.main, 0.08) : open ? t.palette.action.hover : 'transparent'),
          '&:hover': { bgcolor: 'action.hover' },
          '& .rowact': { opacity: 0.35 },
          '&:hover .rowact, &:focus-within .rowact': { opacity: 1 },
        }}
      >
        {readOnly ? null : (
          <IconButton
            ref={setActivatorNodeRef}
            {...attributes}
            size="small"
            aria-label={`Drag ${k}`}
            sx={{ p: 0.25, cursor: 'grab', touchAction: 'none', color: 'text.disabled' }}
          >
            <DragIndicatorIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {selectable ? (
          <Checkbox size="small" checked={selected} onChange={onSelect} inputProps={{ 'aria-label': `Select ${k}` }} sx={{ p: 0 }} />
        ) : (
          <Typography sx={{ width: 20, textAlign: 'right', fontSize: 11, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {index + 1}
          </Typography>
        )}
        <Typography noWrap sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 500, minWidth: 0 }}>{k}</Typography>
        <MiniChip label={type} tone={type === 'boolean' ? 'primary' : 'neutral'} />
        {required && <MiniChip label="required" tone="warning" />}
        {prop[X_MULTILINGUAL] === true && <MiniChip label="i18n" tone="primary" />}
        {prop[X_TEMPLATE] === true && <MiniChip label="template" tone="neutral" />}
        {prop[X_HTML] === true && <MiniChip label="HTML" tone="neutral" />}
        <Typography noWrap variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>{prop.title ?? ''}</Typography>
        {!readOnly && (
          <Box className="rowact" sx={{ display: 'flex', flexShrink: 0 }}>
            <IconButton size="small" onClick={onUp} disabled={upDisabled} aria-label={`Move ${k} up`} sx={{ p: 0.25 }}>
              <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={onDown} disabled={downDisabled} aria-label={`Move ${k} down`} sx={{ p: 0.25 }}>
              <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
        <IconButton
          size="small" onClick={onToggleOpen} aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} details of ${k}`} sx={{ p: 0.25, flexShrink: 0 }}
        >
          <ChevronRightIcon sx={{ fontSize: 16, transition: 'transform 150ms', transform: open ? 'rotate(90deg)' : 'none' }} />
        </IconButton>
      </Box>
      {open && (
        <Box sx={{ ml: 5, mr: 1, mt: 0.25, mb: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

/** Where a drop lands: a line between rows that takes no space. */
function DropLine() {
  return <Box aria-hidden sx={{ height: 2, my: '-1px', mx: 0.75, borderRadius: 1, bgcolor: 'primary.main', position: 'relative', zIndex: 1 }} />;
}

function EmptyDrop({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <Box
      sx={{
        m: 0.5, p: 1.75, textAlign: 'center', fontSize: 12, color: 'text.secondary', borderRadius: 1,
        border: '1.5px dashed', borderColor: active ? 'primary.main' : 'divider',
        bgcolor: (t) => (active ? alpha(t.palette.primary.main, 0.06) : 'transparent'),
      }}
    >
      {children}
    </Box>
  );
}

function CountPill({ text }: { text: string }) {
  return (
    <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, bgcolor: 'action.selected', borderRadius: 999, px: 1, flexShrink: 0 }}>
      {text}
    </Box>
  );
}

function MiniChip({ label, tone }: { label: string; tone: 'primary' | 'warning' | 'neutral' }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10.5, fontWeight: 600, borderRadius: 0.5, px: 0.6, py: '1px', flexShrink: 0,
        color: tone === 'neutral' ? 'text.secondary' : `${tone}.main`,
        bgcolor: (t) => (tone === 'neutral' ? t.palette.action.hover : alpha(t.palette[tone].main, 0.1)),
      }}
    >
      {label}
    </Box>
  );
}
